import {
  useDeleteFolderMutation,
  useGetArchivedWorkspaceFoldersQuery,
  useGetWorkspaceFoldersQuery,
  useGetWorkspaceQuery,
  useLazyGetActiveSharesQuery,
  useUpdateFolderMutation,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  EditIcon,
  MoreVerticalOutlineIcon,
  SettingsIcon,
} from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type WorkspaceFolder,
} from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  type DropdownProps,
  notify,
} from '@carboncopyco/ui';
import { useFolderAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';
import { FolderManageAccessModal } from '../../../../../FolderManageAccessModal';
import { usePermissionsContext } from '../../../../../utils';
import { useWorkspacePageProps } from '../../../WorkspacePageContext';
import { ArchiveFolderModal, FolderModal } from '../../FolderModals';
import { FolderRestore } from './FolderRestore';

interface FolderDropdownProps
  extends Omit<DropdownProps, 'children' | 'triggerElement'> {
  folderName: WorkspaceFolder['name'];
  folderId: WorkspaceFolder['id'];
  onArchiveSuccess?: () => void;
  triggerElement?: ReactNode;
  isArchived?: boolean;
}

export const FolderActions = ({
  folderName,
  folderId,
  onArchiveSuccess,
  isArchived,
  ...dropdownProps
}: FolderDropdownProps) => {
  const { getPermissions } = usePermissionsContext();
  const { organizationId, workspaceId } = useWorkspacePageProps();
  const flags = useFlags();
  const allowToTagAFolder = getPermissions(
    PermissionKey.tag,
    PermissionAction.tagFolder
  );
  const updateFolderByIdPermission = getPermissions(
    PermissionKey.folder,
    PermissionAction.updateFolderById
  );

  const deleteFolderByIdPermission = getPermissions(
    PermissionKey.folder,
    PermissionAction.deleteFolderById
  );
  const restoreFolderPermission = getPermissions(
    PermissionKey.folder,
    PermissionAction.restoreAFolder
  );

  const {
    trackFolderClickConfirmArchive,
    trackFolderClickConfirmArchiveDeactivateLinks,
  } = useFolderAnalytics('archive_folder_modal');
  const [renameFolder] = useUpdateFolderMutation();
  const [archiveFolder] = useDeleteFolderMutation();
  const [isOpenManageAccess, setIsManageAccessOpen] = useBoolean();
  const [showRenameModal, setShowRenameModal] = useBoolean();
  const [showArchiveModal, setShowArchiveModal] = useBoolean();
  const [isLoading, setIsLoading] = useBoolean();

  const handleRenameFolder = (folderName: WorkspaceFolder['name']) => {
    setIsLoading.setTrue();
    renameFolder({
      organizationId,
      workspaceId,
      folderId,
      name: folderName,
    })
      .unwrap()
      .catch(() => {
        notify.error('Unable to rename the folder. Please try again.');
      })
      .finally(() => {
        setShowRenameModal.setFalse();
        setIsLoading.setFalse();
      });
  };

  const { data: activeFolders } = useGetWorkspaceFoldersQuery({
    organizationId,
    limit: 50,
    workspaceId,
  });

  const { data: archivedFolders } = useGetArchivedWorkspaceFoldersQuery({
    organizationId,
    limit: 100,
    workspaceId,
  });
  const activeFoldersNames = activeFolders?.map((folder) => folder?.name);
  const archivedFoldersNames = archivedFolders?.map((folder) => folder?.name);

  const { data: workspace } = useGetWorkspaceQuery({
    organizationId: organizationId,
    id: workspaceId,
  });
  const [getActiveShares, { data: activeShares }] =
    useLazyGetActiveSharesQuery();

  const handleArchiveFolder = () => {
    const analyticsProps = {
      workspaceId,
      folderId,
      workspaceName: workspace?.workspaceName,
      workspaceType: workspace?.workspaceType,
    };
    activeShares?.metadata?.totalItems === 0
      ? trackFolderClickConfirmArchive(analyticsProps)
      : trackFolderClickConfirmArchiveDeactivateLinks(analyticsProps);
    setIsLoading.setTrue();
    archiveFolder({ organizationId, folderId, workspaceId })
      .unwrap()
      .then(() => {
        onArchiveSuccess?.();
        notify.success('Folder was archived successfully.');
      })
      .catch(() => {
        notify.error('Unable to archive this folder. Please try again.');
      })
      .finally(() => {
        setShowArchiveModal.setFalse();
        setIsLoading.setFalse();
      });
  };

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {isArchived && restoreFolderPermission ? (
        <FolderRestore
          folderId={folderId}
          disabled={activeFolders?.length === 50}
        />
      ) : updateFolderByIdPermission || deleteFolderByIdPermission ? (
        <Dropdown
          {...dropdownProps}
          width={200}
          asChild
          triggerElement={
            dropdownProps.triggerElement ?? (
              <Button
                icon={<MoreVerticalOutlineIcon />}
                dark
                xSmall
                aria-label="folder actions menu"
              />
            )
          }
        >
          {updateFolderByIdPermission && (
            <DropdownItem
              iconLeft={<EditIcon size={18} />}
              label="Rename"
              onClick={setShowRenameModal.setTrue}
            />
          )}
          {deleteFolderByIdPermission && (
            <DropdownItem
              iconLeft={<ArchiveIcon size={18} />}
              label="Archive"
              onClick={() => {
                getActiveShares({
                  workspaceId,
                  organizationId,
                  limit: 1,
                  folderIds: [folderId],
                })
                  .unwrap()
                  .then(setShowArchiveModal.setTrue);
              }}
            />
          )}
          {flags.partners && allowToTagAFolder && (
            <DropdownItem
              iconLeft={<SettingsIcon size={18} />}
              label="Manage Access"
              onClick={setIsManageAccessOpen.setTrue}
            />
          )}
        </Dropdown>
      ) : null}
      {showRenameModal && (
        <FolderModal
          onHandleFolderSubmit={handleRenameFolder}
          onClose={setShowRenameModal.setFalse}
          name={folderName}
          activeFoldersNames={activeFoldersNames}
          archivedFoldersNames={archivedFoldersNames}
          isLoading={isLoading}
          show={showRenameModal}
        />
      )}
      {showArchiveModal && (
        <ArchiveFolderModal
          show={showArchiveModal}
          onClose={setShowArchiveModal.setFalse}
          onConfirm={handleArchiveFolder}
          totalActiveShares={activeShares?.metadata?.totalItems}
          isLoading={isLoading}
        />
      )}
      {isOpenManageAccess && (
        <FolderManageAccessModal
          isOpen={isOpenManageAccess}
          onClose={setIsManageAccessOpen.setFalse}
          organizationId={organizationId}
          workspaceId={workspaceId}
          folderId={folderId}
        />
      )}
    </div>
  );
};
